/**
 * CSRF token helper for Frappe API calls.
 *
 * Frappe enforces CSRF on every mutating HTTP method (POST / PUT / PATCH /
 * DELETE); GETs are skipped server-side. The token is session-scoped,
 * stable for the life of the login, and must be sent as the
 * `X-Frappe-CSRF-Token` header.
 */

let _tokenPromise: Promise<string | null> | null = null;

// Whether a real (non-Guest) session exists. The auth layer keeps this in
// sync (setCsrfAuthenticated). When false we MUST NOT call the token endpoint:
// `orgain.user_api.get_csrf_token` is whitelisted but not allow_guest, so a
// Guest call throws a noisy "not whitelisted / Login to access" PermissionError
// (logged server-side, visible in the network tab) during the login ->
// dashboard transition. Gating on this keeps the endpoint authenticated-only
// while silencing that error for Guests.
let _authed = false;

/** Read the CSRF token Frappe injects into the page boot
 *  (`window.csrf_token` in knowledge_app.html). Present and real only for an
 *  authenticated full-page load; `"None"`/empty for a Guest. */
function bootToken(): string | null {
  const t = (window as { csrf_token?: string }).csrf_token;
  return t && t !== 'None' ? t : null;
}

/** Tell the CSRF helper whether an authenticated session exists. Called by the
 *  auth layer on login / session-check / logout. When set false, any cached
 *  token is dropped so the next authenticated call fetches fresh. */
export function setCsrfAuthenticated(value: boolean): void {
  _authed = value;
  if (!value) _tokenPromise = null;
}

/** Return the current session's CSRF token, cached after the first call.
 *  Returns null if the user isn't authenticated yet - callers should treat
 *  that as "omit the header" rather than "fail". */
export async function getCsrfToken(): Promise<string | null> {
  // No known session: never hit the (non-guest) endpoint - that would raise a
  // PermissionError for a Guest. Fall back to the boot token if this was an
  // authenticated full-page load; otherwise null (caller omits the header).
  if (!_authed) return bootToken();

  if (_tokenPromise) return _tokenPromise;
  const pending = (async () => {
    try {
      // Custom wrapper in orgain.user_api - Frappe's frappe.sessions.get_csrf_token
      // is not whitelisted in v15, so we expose it via our own endpoint.
      const r = await fetch('/api/method/orgain.user_api.get_csrf_token', {
        credentials: 'include',
      });
      if (!r.ok) return null;
      const j = (await r.json()) as { message?: string };
      return j?.message || null;
    } catch {
      return null;
    }
  })();
  _tokenPromise = pending;
  // Never let a failed/guest fetch (null) poison the cache for the whole page
  // lifetime - that would omit the header on every later mutating request and
  // force a manual refresh. Only keep a real token; otherwise reset so the next
  // call retries.
  pending.then((token) => {
    if (!token && _tokenPromise === pending) {
      _tokenPromise = null;
    }
  });
  return pending;
}

/** Invalidate the cached token. Call on logout so the next login picks up
 *  the new session's token. */
export function clearCsrfToken(): void {
  _tokenPromise = null;
}
